// Klient API stavu objednávky (#461, search/api/order-status.mjs na s1).
//
// Vlastní, ne `searchApi.js`: ten zná jen GET, s chybou shodí breaker celé
// vrstvy hledání a `guardOrigins` by přepsal odkaz dopravce na relativní.
// Tady jde o jeden POST na akci zákazníka:
//   · tělo `text/plain` (CORS-simple, žádný preflight), `credentials: 'omit'`;
//   · číslo objednávky ani e-mail nikdy v URL (request log API = URL + IP);
//   · chyby se jen roztřídí pro stránku, nic se nevypíná.
// Doklad (route B) se stahuje týmž způsobem jako formulář `x-www-form-urlencoded`
// přes fetch: chybu (JSON) pak ukáže stránka, neotevře se prázdná karta s JSONem.

const TIMEOUT_MS = 10000;
const DOC_TIMEOUT_MS = 20000;
// storno: čerstvý detail, zásilky, zápis stavu (Shoptet posílá e-mail) a poznámka, API 15 s
const CANCEL_TIMEOUT_MS = 20000;

function endpoint(config, tail) {
  return config.api + '/v1/' + encodeURIComponent(config.tenant) + '/order-status' + (tail || '');
}

function withTimeout(ms, run) {
  const ctrl = new AbortController();
  const timer = setTimeout(function () {
    ctrl.abort();
  }, ms);
  return run(ctrl.signal).then(
    function (v) {
      clearTimeout(timer);
      return v;
    },
    function (e) {
      clearTimeout(timer);
      throw e;
    }
  );
}

/**
 * Chyba API → `{ok:false, error, retryAfter}`:
 * `not_found` (404, i chybný e-mail), `locked` (429 zámek nebo limit routy),
 * `outage` (503 busy/upstream/not_configured, 5xx, síť, timeout).
 */
export function classify(status, body) {
  const err = (body && body.error) || {};
  const retry = Number(err.retry_after) > 0 ? Number(err.retry_after) : null;
  if (status === 404 || status === 400) return { ok: false, error: 'not_found', retryAfter: null };
  if (status === 429) return { ok: false, error: 'locked', retryAfter: retry || 60 };
  return { ok: false, error: 'outage', retryAfter: retry };
}

/**
 * `POST /v1/{tenant}/order-status` s `{code, email}`.
 * @returns {Promise<{ok:true, data:object}|{ok:false, error:string, retryAfter:number|null}>}
 */
export function lookupOrder(config, code, email) {
  return withTimeout(TIMEOUT_MS, function (signal) {
    return window
      .fetch(endpoint(config), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({ code: code, email: email }),
        signal: signal,
      })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            if (res.status === 200 && body && body.order) return { ok: true, data: body };
            return classify(res.status, body);
          });
      });
  }).catch(function () {
    return { ok: false, error: 'outage', retryAfter: null };
  });
}

/**
 * Samoobslužné storno (`POST …/order-status/cancel` s `{code, email, confirm: true}`).
 * Chyby navíc proti hledání: `not_cancellable` (409, objednávka mezitím šla dál nebo
 * byla zaplacená), `conflict` (409, Shoptet ji měnil ve stejné chvíli, zkusit znovu).
 * @returns {Promise<{ok:true}|{ok:false, error:string, retryAfter:number|null}>}
 */
export function cancelOrder(config, code, email) {
  return withTimeout(CANCEL_TIMEOUT_MS, function (signal) {
    return window
      .fetch(endpoint(config, '/cancel'), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({ code: code, email: email, confirm: true }),
        signal: signal,
      })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            if (res.status === 200 && body && body.cancelled === true) return { ok: true };
            const err = (body && body.error && body.error.code) || '';
            if (res.status === 409) return { ok: false, error: err === 'conflict' ? 'conflict' : 'not_cancellable', retryAfter: null };
            if (res.status === 403) return { ok: false, error: 'not_cancellable', retryAfter: null };
            return classify(res.status, body);
          });
      });
  }).catch(function () {
    return { ok: false, error: 'outage', retryAfter: null };
  });
}

/**
 * Doklad PDF (`POST …/order-status/document`) → `{ok:true, blob}`, nebo chyba roztříděná
 * jako u hledání (`locked` = limit API při stahování víc dokladů rychle za sebou).
 * Formulářové tělo je CORS-simple stejně jako `text/plain`.
 */
export function fetchDocument(config, code, email, doc) {
  const body = new URLSearchParams();
  body.set('code', code);
  body.set('email', email);
  body.set('doc', doc.code);
  body.set('type', doc.type);
  return withTimeout(DOC_TIMEOUT_MS, function (signal) {
    return window
      .fetch(endpoint(config, '/document'), { method: 'POST', mode: 'cors', credentials: 'omit', body: body, signal: signal })
      .then(function (res) {
        const type = res.headers.get('content-type') || '';
        if (res.ok && /application\/pdf/i.test(type)) {
          return res.blob().then(function (blob) {
            return { ok: true, blob: blob };
          });
        }
        return classify(res.status, null);
      });
  }).catch(function () {
    return { ok: false, error: 'outage', retryAfter: null };
  });
}
